"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronRight, ChevronLeft, Check } from "lucide-react";

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, opts: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

type Step = "details" | "waiver" | "submitting" | "success";

const VOLUNTEER_SHIFT_OPTIONS = [
  { value: "8am-10am", label: "8:00 AM – 10:00 AM", desc: "Setup & check-in" },
  { value: "10am-12pm", label: "10:00 AM – 12:00 PM", desc: "Parade route support" },
  { value: "11am-1pm", label: "11:00 AM – 1:00 PM", desc: "Route & cleanup" },
];

const WAIVER_TEXT = `RELEASE AND ASSUMPTION OF RISK

THE CITY OF SACRAMENTO IS REQUIRING ALL PARADE PARTICIPANTS TO ASSUME ALL RISKS OF INJURY AND DAMAGE TO ATTEND THIS SPECIAL EVENT

On behalf of myself, my organization, and all persons who will be on the vehicle/float sponsored by myself or my organization or who will be marching, riding or otherwise participating in the City of Sacramento's Fourth of July parade to be held on July 4, 2026 (the "Special Event"); I/we hereby voluntarily waive, release, and discharge the CITY OF SACRAMENTO ("City") and its respective officers, agents, employees, contractors, and volunteers, (collectively "Releasees") from all claims, actions, or demands of any kind, nature, and description (collectively "Claims"), including Claims for damages for personal injury, including death, and property damage, which I and any person from my organization including volunteers may have now or in the future have against Releasees arising out of or in any way related to or connected with our participation at the Special Event by the action or inaction of Releasees or any other person present at the Special Event.

This release is intended to discharge, in advance, the City from any and all liability arising out of or connected in any way with our participation in the parade and any activities offered during the Special Event, even though that liability may arise out of negligence or carelessness on the part of Releasees. I further understand and agree that this Release and Assumption of Risk is to be binding on our respective heirs, executors, administrators and assigns.

I understand that serious accidents, including grave personal injury or death, can occur from time to time from Special Event activities. I have read this Release and Assumption of Risk and understand that I am giving up substantial rights by signing it, and I am hereby attesting to the fact that I signed it voluntarily. I also attest that I have informed all of the other persons associated with my organization who will participate in the Special Event regarding the fact that this Release and Assumption of Risk also applies to them individually.`;

export function RegisterForm({ regType }: { regType: "parade-entry" | "volunteer" }) {
  const [step, setStep] = useState<Step>("details");
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);

  // Turnstile — the widget div only exists once the user advances to the
  // waiver step, so this effect runs on every step change and polls until
  // both window.turnstile and the div are present.
  useEffect(() => {
    if (step !== "waiver" && step !== "submitting") return;
    if (widgetIdRef.current) return;

    const tryRender = (): boolean => {
      if (widgetIdRef.current) return true;
      if (!turnstileRef.current || !window.turnstile) return false;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: "0x4AAAAAAB7-7KQO4wfPGwUh",
        callback: (token: string) => { tokenRef.current = token; },
        "expired-callback": () => { tokenRef.current = null; },
        "error-callback": () => { tokenRef.current = null; },
        theme: "light",
      });
      return true;
    };

    if (tryRender()) return;
    const interval = setInterval(() => {
      if (tryRender()) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [step]);

  const getToken = useCallback((): Promise<string | null> => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current);
    return new Promise((resolve) => {
      let elapsed = 0;
      const poll = setInterval(() => {
        if (tokenRef.current) { clearInterval(poll); resolve(tokenRef.current); }
        elapsed += 200;
        // 20s allows iOS PAT 401 → interactive-challenge fallback to complete
        if (elapsed > 20000) { clearInterval(poll); resolve(null); }
      }, 200);
    });
  }, []);

  const handleSubmit = async () => {
    const form = formRef.current;
    if (!form) return;

    const waiverSigned = form.querySelector<HTMLInputElement>('[name="waiver_signed"]');
    const waiverName = form.querySelector<HTMLInputElement>('[name="waiver_signer_name"]');
    if (!waiverSigned?.checked) {
      setError("You must agree to the liability waiver to register.");
      return;
    }
    if (!waiverName?.value.trim()) {
      setError("Please type your full legal name to sign the waiver.");
      return;
    }

    setError("");
    setStep("submitting");

    try {
      const token = await getToken();
      if (!token) {
        setError("We couldn't verify you're human. Please complete the verification challenge below, or disable any ad blockers and try again.");
        setStep("waiver");
        return;
      }
      const formData = new FormData(form);
      formData.set("cf-turnstile-response", token);

      const res = await fetch("/api/submit/register", {
        method: "POST",
        body: formData,
      });

      const url = res.url || "";
      if (url.includes("submitted=1")) {
        setStep("success");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (url.includes("error=")) {
        const errParam = new URL(url).searchParams.get("error");
        setError(errParam || "Something went wrong.");
        setStep("waiver");
      } else {
        setError("Something went wrong. Please try again.");
        setStep("waiver");
      }
    } catch (err: any) {
      setError(err?.message || "Something went wrong.");
      setStep("waiver");
    } finally {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        tokenRef.current = null;
      }
    }
  };

  if (step === "success") {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-grass-green/10 flex items-center justify-center mx-auto mb-5">
          <Check className="w-8 h-8 text-grass-green" />
        </div>
        <h2 className="text-2xl mb-3">Registration Complete!</h2>
        <p className="text-bunting-navy/60 max-w-md mx-auto mb-2">
          Thank you for registering for the 2026 Pocket Parade. A confirmation email with your details has been sent.
        </p>
        <p className="text-sm text-bunting-navy/40">
          Check your email for important reminders about check-in times and parade day logistics.
        </p>
        <p className="text-xs text-bunting-navy/30 mt-6">
          Didn't receive a confirmation email? Please allow up to 1 hour for delivery, then contact{" "}
          <a href="mailto:info@july4thpocketparade.com" className="text-summer-sky hover:underline">info@july4thpocketparade.com</a>
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} method="POST" action="/api/submit/register" onSubmit={(e) => e.preventDefault()}>
      <input type="hidden" name="registration_type" value={regType} />

      {/* Step 1: Details — always in DOM so FormData captures all fields */}
      <div className={step === "details" ? "" : "hidden"}>
          <StepIndicator step={1} total={2} />

          {/* Contact info */}
          <fieldset className="mb-8">
            <legend className="font-display font-bold text-sm text-liberty-blue uppercase tracking-wider mb-4">Contact Information</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="form-field">
                <label htmlFor="first_name" className="form-label">First name *</label>
                <input id="first_name" name="first_name" type="text" required className="form-input" placeholder="Jane" />
              </div>
              <div className="form-field">
                <label htmlFor="last_name" className="form-label">Last name *</label>
                <input id="last_name" name="last_name" type="text" required className="form-input" placeholder="Smith" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="form-field">
                <label htmlFor="email" className="form-label">Email *</label>
                <input id="email" name="email" type="email" required className="form-input" placeholder="jane@example.com" />
              </div>
              <div className="form-field">
                <label htmlFor="phone" className="form-label">Phone</label>
                <input id="phone" name="phone" type="tel" className="form-input" placeholder="(916) 555-1234" />
              </div>
            </div>
            <div className="form-field mb-4">
              <label className="flex items-start gap-2 text-sm text-bunting-navy/70 cursor-pointer">
                <input type="checkbox" name="sms_consent" className="mt-1" />
                <span>Text me a confirmation. Message and data rates may apply. Reply STOP to opt out.</span>
              </label>
            </div>
            <div className="form-field">
              <label htmlFor="organization_name" className="form-label">Organization name (if applicable)</label>
              <input id="organization_name" name="organization_name" type="text" className="form-input" placeholder="Girl Scouts Troop 1408, Rotary Club, etc." />
            </div>
          </fieldset>

          {/* Parade entry specific */}
          {regType === "parade-entry" && (
            <fieldset className="mb-8">
              <legend className="font-display font-bold text-sm text-liberty-blue uppercase tracking-wider mb-4">Entry Details</legend>

              <div className="form-field mb-4">
                <label htmlFor="entry_type" className="form-label">Entry type *</label>
                <select id="entry_type" name="entry_type" required className="form-input">
                  <option value="">Select one...</option>
                  <option value="individual">Individual Entry</option>
                  <option value="group">Group Entry</option>
                  <option value="organization">Organization Entry</option>
                  <option value="car-club">Car Club Entry</option>
                </select>
                <div className="mt-2 text-xs text-bunting-navy/50 space-y-1">
                  <p><strong>Individual</strong> — A single person or family entering on their own (one vehicle or walking)</p>
                  <p><strong>Group</strong> — An informal group of people entering together (neighborhood friends, sports team, etc.)</p>
                  <p><strong>Organization</strong> — A registered business, nonprofit, school, church, or civic group</p>
                  <p><strong>Car Club</strong> — A car club may register up to <strong>12 vehicles</strong> under a single entry</p>
                </div>
              </div>

              <div className="form-field mb-4">
                <label htmlFor="entry_name" className="form-label">Entry name (as announced by the emcee) *</label>
                <input id="entry_name" name="entry_name" type="text" required className="form-input" placeholder="e.g. Girl Scouts Brownie Troop 1408" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                <div className="form-field">
                  <label className="form-label flex items-center gap-2">
                    <input type="checkbox" name="has_vehicle" className="rounded" />
                    Vehicle
                  </label>
                  <span className="text-xs text-bunting-navy/40">Limit 1 (car clubs: up to 12)</span>
                </div>
                <div className="form-field">
                  <label className="form-label flex items-center gap-2">
                    <input type="checkbox" name="has_trailer" className="rounded" />
                    Trailer (no float)
                  </label>
                  <span className="text-xs text-bunting-navy/40">Limit 1</span>
                </div>
                <div className="form-field">
                  <label className="form-label flex items-center gap-2">
                    <input type="checkbox" name="has_float" className="rounded" />
                    Float
                  </label>
                  <span className="text-xs text-bunting-navy/40">Requires fire safety review</span>
                </div>
              </div>

              <div className="bg-sunset-orange/10 border border-sunset-orange/20 rounded-lg p-4 mb-4 text-sm text-bunting-navy/70">
                <p className="font-semibold text-bunting-navy mb-1">Float entries: fire safety requirement</p>
                <p>All float entries must pass additional fire safety requirements. Please email your float design to{" "}
                  <a href="mailto:info@july4thpocketparade.com?subject=Float%20Design%20-%20Fire%20Safety%20Review" className="text-summer-sky hover:underline font-medium">
                    info@july4thpocketparade.com
                  </a>{" "}
                  for review before the parade. Floats that have not been reviewed may not be permitted to participate.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="form-field">
                  <label htmlFor="num_bikes_scooters" className="form-label"># Bikes/Scooters</label>
                  <input id="num_bikes_scooters" name="num_bikes_scooters" type="number" min="0" className="form-input" defaultValue="0" />
                </div>
                <div className="form-field">
                  <label htmlFor="num_walkers" className="form-label"># Walkers</label>
                  <input id="num_walkers" name="num_walkers" type="number" min="0" className="form-input" defaultValue="0" />
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="entry_description" className="form-label">
                  Description (read by the emcee at the reviewing stand) *
                </label>
                <textarea
                  id="entry_description"
                  name="entry_description"
                  required
                  rows={4}
                  className="form-input resize-none"
                  placeholder="A brief description of your entry for the parade announcer..."
                />
              </div>
            </fieldset>
          )}

          {/* Volunteer specific */}
          {regType === "volunteer" && (
            <fieldset className="mb-8">
              <legend className="font-display font-bold text-sm text-liberty-blue uppercase tracking-wider mb-4">Volunteer Preferences</legend>

              <div className="form-field mb-4">
                <label className="form-label mb-3">What hours are you available? (select all that apply)</label>
                <p className="text-xs text-bunting-navy/40 mb-3">We need volunteers across all shifts — please select as many as you can!</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {VOLUNTEER_SHIFT_OPTIONS.map((shift) => (
                    <label key={shift.value} className="flex items-start gap-2.5 bg-liberty-blue/5 border border-liberty-blue/10 rounded-lg p-3 cursor-pointer hover:bg-liberty-blue/10 transition-colors">
                      <input type="checkbox" name={`shift_${shift.value}`} defaultChecked className="rounded mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-bunting-navy block">{shift.label}</span>
                        <span className="text-xs text-bunting-navy/50">{shift.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="volunteer_notes" className="form-label">Anything else we should know?</label>
                <textarea
                  id="volunteer_notes"
                  name="volunteer_notes"
                  rows={3}
                  className="form-input resize-none"
                  placeholder="Experience, availability, special skills, etc."
                />
              </div>
            </fieldset>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                const form = formRef.current;
                if (!form?.reportValidity()) return;
                if (regType === "volunteer") {
                  const roles = VOLUNTEER_SHIFT_OPTIONS
                    .filter((s) => form.querySelector<HTMLInputElement>(`[name="shift_${s.value}"]`)?.checked)
                    .map((s) => s.value)
                    .join(",");
                  let hidden = form.querySelector<HTMLInputElement>('[name="volunteer_roles"]');
                  if (!hidden) {
                    hidden = document.createElement("input");
                    hidden.type = "hidden";
                    hidden.name = "volunteer_roles";
                    form.appendChild(hidden);
                  }
                  hidden.value = roles;
                }
                setStep("waiver");
              }}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              Continue to Waiver <ChevronRight className="w-4 h-4 shrink-0" />
            </button>
          </div>
      </div>

      {/* Step 2: Waiver */}
      {(step === "waiver" || step === "submitting") && (
        <div>
          <StepIndicator step={2} total={2} />

          <div className="bg-white border border-liberty-blue/10 rounded-xl p-6 mb-6">
            <h3 className="text-base font-bold mb-3 text-parade-red">Release and Assumption of Risk</h3>
            <p className="text-xs text-bunting-navy/50 italic mb-3">This is a release of liability. Read carefully before signing.</p>
            <div className="max-h-64 overflow-y-auto border border-liberty-blue/10 rounded-lg p-4 bg-star-white text-xs text-bunting-navy/70 leading-relaxed whitespace-pre-wrap mb-6">
              {WAIVER_TEXT}
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="waiver_signed" className="mt-1 rounded" required />
                <span className="text-sm text-bunting-navy">
                  I have read the Release and Assumption of Risk, understand that I am giving up substantial rights by signing it, and sign it voluntarily. I attest that I have informed all persons associated with my organization that this release applies to them individually.
                </span>
              </label>

              <div className="form-field">
                <label htmlFor="waiver_signer_name" className="form-label">Full legal name (electronic signature) *</label>
                <input
                  id="waiver_signer_name"
                  name="waiver_signer_name"
                  type="text"
                  required
                  className="form-input font-accent text-xl italic"
                  placeholder="Type your full name"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-4">
              {error}
            </div>
          )}

          <div ref={turnstileRef} className="mb-4" />

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep("details")}
              className="btn-secondary flex items-center gap-2"
              disabled={step === "submitting"}
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={step === "submitting"}
              className="btn-primary flex items-center gap-2"
            >
              {step === "submitting" ? "Submitting..." : "Submit Registration"}
              {step !== "submitting" && <Check className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}

function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            i + 1 <= step ? "bg-liberty-blue text-white" : "bg-liberty-blue/10 text-bunting-navy/40"
          }`}>
            {i + 1 < step ? <Check className="w-4 h-4" /> : i + 1}
          </div>
          {i < total - 1 && (
            <div className={`w-12 h-0.5 ${i + 1 < step ? "bg-liberty-blue" : "bg-liberty-blue/10"}`} />
          )}
        </div>
      ))}
      <span className="text-xs text-bunting-navy/40 ml-2">
        Step {step} of {total}
      </span>
    </div>
  );
}
